import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { Button, EmptyState } from '../../../components/ui';
import { ROUTES } from '../../../constants/routes';
import { buildBreadcrumbs, createBreadcrumbNavigateHandler } from '../../../config/navigation/breadcrumbs';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider';
import { authzContextAtom } from '../../../authorization/authzState';
import { canAccessResource } from '../../../authorization/canAccessResource';
import { userInformationAtom } from '../../../Services/_states';
import BusinessFunctionCard, { isHubAccountActive } from '../components/BusinessFunctionCard';
import BusinessFunctionOrgFilter from '../components/BusinessFunctionOrgFilter';
import CreateBusinessFunctionDialog from '../components/CreateBusinessFunctionDialog';
import { ACCOUNT_STATUS_COPY } from '../constants/accountConstants';
import useBusinessFunctionsSx from '../hooks/useBusinessFunctionsSx';
import useOrganisationAccounts from '../hooks/useOrganisationAccounts';
import { accountService } from '../services/accountService';
import { organisationAccountsRefreshAtom } from '../state/accountAtoms';
import { canCreateBusinessFunction } from '../utils/canCreateBusinessFunction';

function readToggleError(err) {
  const raw =
    err?.response?.data?.data?.general_error ||
    err?.response?.data?.data?.system_error ||
    err?.response?.data?.message ||
    err?.message ||
    ACCOUNT_STATUS_COPY.toggleFailed;
  return typeof raw === 'string' ? raw : ACCOUNT_STATUS_COPY.toggleFailed;
}

function BusinessFunctionsHubContent() {
  const styles = useBusinessFunctionsSx();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSnackBar } = useSnackBar();
  const authz = useRecoilValue(authzContextAtom);
  const userInfo = useRecoilValue(userInformationAtom);
  const bumpAccountsRefresh = useSetRecoilState(organisationAccountsRefreshAtom);
  const loggedInOrganisationId =
    userInfo?.profile?.organisation?.organisation_id ||
    userInfo?.profile?.organisation_id ||
    '';
  const loggedInOrganisationName = userInfo?.profile?.organisation?.name || '';

  const canCreate = canCreateBusinessFunction(userInfo);
  const [organisationFilter, setOrganisationFilter] = useState(null);
  const listOrganisationId = canCreate
    ? (organisationFilter?.organisation_id || organisationFilter?.id || '')
    : loggedInOrganisationId;

  const { accounts, isLoading, error, refetch, patchAccountInList } = useOrganisationAccounts(
    listOrganisationId,
    { allowAll: canCreate }
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [togglingAccountId, setTogglingAccountId] = useState('');

  const selectedOrganisationId = organisationFilter?.organisation_id || organisationFilter?.id || '';
  const selectedOrganisationName = organisationFilter?.name || organisationFilter?.label || '';
  const createOrganisationId = canCreate ? selectedOrganisationId : loggedInOrganisationId;
  const createOrganisationName = canCreate ? selectedOrganisationName : loggedInOrganisationName;
  const viewingAllOrganisations = canCreate && !listOrganisationId;

  const visibleAccounts = useMemo(() => {
    return (accounts || []).filter((account) => {
      if (!account?.account_id) return false;
      if (!canCreate && !isHubAccountActive(account)) return false;
      if (canCreate || !authz.isAuthzV2Enabled || authz.isFullAccess) return true;
      return canAccessResource(authz, {
        type: 'account',
        id: account.account_id,
        action: 'view',
      });
    });
  }, [accounts, authz, canCreate]);

  const openCreateDialog = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  const closeCreateDialog = useCallback(() => {
    setCreateDialogOpen(false);
    if (searchParams.get('create') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('create') === '1' && canCreate && !isLoading) {
      openCreateDialog();
    }
  }, [canCreate, isLoading, openCreateDialog, searchParams]);

  const breadcrumbsData = useMemo(
    () => buildBreadcrumbs([{ label: 'Business Functions' }]),
    []
  );

  const openAccount = (account) => {
    const name = account.name || account.account_name || 'Account';
    navigate(
      `${ROUTES.CAMPAIGN.BUSINESS_FUNCTIONS}/${encodeURIComponent(account.account_id)}?name=${encodeURIComponent(name)}`
    );
  };

  const handleCreated = async (createdAccount) => {
    bumpAccountsRefresh((value) => value + 1);
    await refetch();

    const accountId = createdAccount?.account_id;
    if (accountId) {
      openAccount(createdAccount);
    }
  };

  const handleToggle = useCallback(async (account, nextActive) => {
    const accountId = account?.account_id;
    if (!accountId || !canCreate) return;

    const previousActive = isHubAccountActive(account);
    if (previousActive === nextActive) return;

    setTogglingAccountId(accountId);
    patchAccountInList(accountId, { is_active: nextActive });

    try {
      const res = await accountService.setAccountActive(accountId, nextActive);
      if (res?.status !== 200 || res?.data?.status === false) {
        throw new Error(
          res?.data?.data?.general_error || ACCOUNT_STATUS_COPY.toggleFailed
        );
      }
      showSnackBar(
        nextActive ? ACCOUNT_STATUS_COPY.activated : ACCOUNT_STATUS_COPY.deactivated,
        'success'
      );
      bumpAccountsRefresh((value) => value + 1);
      await refetch();
    } catch (err) {
      patchAccountInList(accountId, { is_active: previousActive });
      showSnackBar(readToggleError(err), 'error');
    } finally {
      setTogglingAccountId('');
    }
  }, [bumpAccountsRefresh, canCreate, patchAccountInList, refetch, showSnackBar]);

  return (
    <Box sx={styles.pageRoot}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={createBreadcrumbNavigateHandler(navigate)}
      />

      <Box sx={styles.pageHeader}>
        <Typography variant="h5" sx={styles.pageTitle}>
          Business Functions
        </Typography>
        <Typography sx={styles.pageSubtitle}>
          {viewingAllOrganisations
            ? 'Organise business functions and services across all organisations.'
            : `Organise business functions and services for ${createOrganisationName || 'your organisation'}.`}
        </Typography>
      </Box>
      {canCreate ? (
        <Box sx={styles.hubActionRow}>
          <BusinessFunctionOrgFilter
            value={organisationFilter}
            onChange={setOrganisationFilter}
            sx={styles.hubOrgFilter}
          />
          <Button
            variant="primary"
            icon={<AddIcon fontSize="small" />}
            onClick={openCreateDialog}
            sx={{ justifySelf: 'end' }}
          >
            Add New Business Function
          </Button>
        </Box>
      ) : null}

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
          <CircularProgress size={22} />
          <Typography>Loading business functions...</Typography>
        </Box>
      ) : null}

      {!isLoading && error ? (
        <Typography color="error">{error}</Typography>
      ) : null}

      {!isLoading && !error && visibleAccounts.length === 0 ? (
        <EmptyState
          icon={<StorefrontOutlinedIcon fontSize="inherit" />}
          title="No business functions yet"
          description={
            canCreate
              ? 'Create your first business function to get started. Business services can be added under each function.'
              : 'No business functions are available for this profile yet.'
          }
          action={
            canCreate ? (
              <Button variant="primary" icon={<AddIcon fontSize="small" />} onClick={openCreateDialog}>
                Create business function
              </Button>
            ) : null
          }
          sx={styles.workspaceEmptyRoot}
        />
      ) : null}

      {!isLoading && visibleAccounts.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: 2,
            overflowY:'scroll',
            maxHeight:'calc(100vh - 283px)',
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {visibleAccounts.map((account) => (
            <BusinessFunctionCard
              key={account.account_id}
              account={account}
              canToggle={canCreate}
              isToggling={togglingAccountId === account.account_id}
              showOrganisation={viewingAllOrganisations}
              onOpen={openAccount}
              onToggle={handleToggle}
              styles={styles}
            />
          ))}
        </Box>
      ) : null}

      <CreateBusinessFunctionDialog
        open={createDialogOpen}
        organisationId={createOrganisationId}
        organisationName={createOrganisationName}
        onClose={closeCreateDialog}
        onCreated={handleCreated}
      />
    </Box>
  );
}

export default function BusinessFunctionsHubPage() {
  return (
    <FeatureThemeProvider>
      <BusinessFunctionsHubContent />
    </FeatureThemeProvider>
  );
}
